import React, { FC, ReactElement, memo } from "react";
import { Divider, Stack, SxProps, Typography } from "@mui/material";
import ScoringRating from "./ScoringRating";
import ScoringRatingChip from "./ScoringRatingChip";
import { noop } from "lodash";

interface IScoringRatingPanelProps {
    ratings: number[];
    ratingCriteria: string[];
    headerText?: string | ReactElement;
    readOnly?: boolean;
    hideHeader?: boolean;
    hideRatingChip?: boolean;
    onRatingChange?: (index: number, value: number) => void;
    sx?: SxProps;
    size?: "small" | "medium" | "large";
}

const ScoringRatingPanel: FC<IScoringRatingPanelProps> = (props) => {
    const {
        ratings,
        ratingCriteria,
        headerText,
        readOnly,
        hideHeader,
        hideRatingChip,
        onRatingChange,
        sx,
        size,
    } = props;
    const avgRating =
        ratingCriteria?.length > 0
            ? ratings.reduce(
                    (sum, rating) => sum + rating / ratingCriteria?.length,
                    0
                )
            : 0;

    if (ratingCriteria?.length === 0) {
        return null;
    }

    return (
        <Stack spacing={2} sx={sx}>
            {!hideHeader && (
                <Stack
                    direction="row"
                    sx={{ alignItems: "center" }}
                    spacing={2}
                >
                    <Typography
                        fontWeight={700}
                        sx={{ lineHeight: 1, flexGrow: 1 }}
                    >
                        {headerText}
                    </Typography>
                    {!hideRatingChip && (
                        <ScoringRatingChip
                            rating={avgRating}
                            label="Avg.: "
                        />
                    )}
                </Stack>
            )}
            <Divider />
            {ratingCriteria?.map((question, index) => (
                <Stack
                    key={question}
                    direction="row"
                    sx={{ alignItems: "center", justifyContent: "space-between" }}
                    spacing={5}
                >
                    <Typography>{question}</Typography>
                    <ScoringRating
                        index={index}
                        readOnly={readOnly}
                        value={ratings[index] || 0}
                        onChange={(index, value) => onRatingChange(index, value)}
                        size={size}
                    />
                </Stack>
            ))}
        </Stack>
    );
};

ScoringRatingPanel.defaultProps = {
    ratings: [],
    ratingCriteria: [],
    headerText: "",
    readOnly: false,
    hideHeader: false,
    hideRatingChip: false,
    onRatingChange: noop,
    sx: {},
    size: "medium",
};

export default memo(ScoringRatingPanel);
